/**
 * Данные экрана «Структура посевных площадей» (СПП) планового сезона.
 *
 * Экран — СРЕЗ варианта плана, а не отдельная сущность (решение founder
 * 18.08): своих записей у него нет, он читает готовый агрегат
 * `GET /api/v1/planning/scenarios/{id}/crop-structure` и раскладывает его в
 * таблицу той же формы, что живой экран структуры на дашборде.
 *
 * Хелперы форматирования держим здесь (а не в компоненте) ровно затем, чтобы
 * их можно было проверить тестами без рендера: «доля от пашни», «изменение
 * против факта» и «почему цифры нет» — это как раз то, за что агроном ловит
 * систему на вранье.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cropStructureKeys, variantListKeys } from '@/lib/planning/queryKeys';

export type PlanSource = 'fields' | 'tech_cards' | 'empty';
export type RowKind = 'crop' | 'fallow' | 'unassigned' | 'dropped';

export interface CropStructureRow {
  key: string;
  crop_id: string | null;
  name: string;
  kind: RowKind;
  fields_count: number | null;
  area_ha: number | null;
  share_pct: number | null;
  fact_area_ha: number | null;
  fact_fields_count: number | null;
  delta_ha: number | null;
  /** Пусто и когда права на техкарты нет — тогда `economics_visible=false`. */
  yield_c_ha: number | null;
  gross_tons: number | null;
  tech_cards_count: number | null;
  /** Чего не хватило для честной цифры — показываем текстом, не пустой ячейкой. */
  gap: string | null;
}

export interface RotationNote {
  code: 'repeat_sowing' | 'early_return';
  severity: 'alert';
  title: string;
  detail: string;
  fields_count: number;
  area_ha: number;
  field_names: string[];
}

export interface CropStructure {
  scenario_id: string;
  scenario_name: string;
  is_selected: boolean;
  farm_id: string;
  farm_name: string;
  season_id: string;
  season_year: number;
  prev_season_id: string | null;
  prev_season_year: number | null;
  plan_source: PlanSource;
  /** Видны ли нормативы техкарт (право `tech_cards:R`). */
  economics_visible: boolean;
  arable_ha: number;
  arable_fields_count: number;
  plan_area_ha: number;
  crops_area_ha: number;
  fallow_ha: number;
  /** Пашня без плана. Ноль, если план ПЕРЕкрывает пашню — см. `overplanned_ha`. */
  unassigned_ha: number;
  /** На сколько план больше пашни. Ноль — нормальный случай. */
  overplanned_ha: number;
  plan_fields_count: number | null;
  gross_tons: number | null;
  crops_count: number;
  items: CropStructureRow[];
  rotation_notes: RotationNote[];
  rotation_basis: 'ok' | 'no_field_plan';
}

/** Вариант плана хозяйства на сезон (`scenarios`, ADR-045). */
export interface SppPlanVariant {
  id: string;
  farm_id: string;
  season_id: string;
  name: string;
  status: string;
  is_selected: boolean;
  planned_area_ha: number;
}

export const sppKeys = {
  // Список вариантов — ОБЩИЙ ключ с экраном назначения техкарт (queryKeys.ts):
  // после «Создать вариант плана» одна инвалидация обновляет оба экрана.
  variants: variantListKeys.list,
  // Ключ структуры тоже общий (queryKeys.ts): его инвалидирует заведение
  // культуры в план с соседнего экрана.
  structure: cropStructureKeys.structure,
};

export function useSppPlanVariants(farmId: string | null, seasonId: string | null) {
  return useQuery<SppPlanVariant[], Error>({
    queryKey: sppKeys.variants(farmId, seasonId),
    queryFn: () =>
      api.get<SppPlanVariant[]>(
        `/api/v1/planning/scenarios?farm_id=${farmId}&season_id=${seasonId}`,
      ),
    enabled: !!farmId && !!seasonId,
    staleTime: 30_000,
  });
}

export function useCropStructure(scenarioId: string | null) {
  return useQuery<CropStructure, Error>({
    queryKey: sppKeys.structure(scenarioId),
    queryFn: () =>
      api.get<CropStructure>(`/api/v1/planning/scenarios/${scenarioId}/crop-structure`),
    enabled: !!scenarioId,
    staleTime: 30_000,
  });
}

// ── Форматирование ───────────────────────────────────────────────────────────

const DASH = '—';

/** Гектары без дробной части: агроном оперирует целыми га, а не сотками. */
export function formatHa(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return Math.round(value).toLocaleString('ru-RU');
}

export function formatShare(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} %`;
}

/** Изменение против прошлого сезона: со знаком, ноль — это «столько же». */
export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  const rounded = Math.round(value);
  if (rounded === 0) return '0';
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toLocaleString('ru-RU')}`;
}

export function formatYield(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
}

export function formatTons(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return Math.round(value).toLocaleString('ru-RU');
}

/** Ширина полоски доли — доля от пашни, но не шире самой полоски. */
export function shareBarWidth(sharePct: number | null | undefined): string {
  if (!sharePct || sharePct <= 0) return '0%';
  return `${Math.min(100, sharePct)}%`;
}

/**
 * Откуда взяты гектары. Врать «у вас 0 га» нельзя: пока план собран
 * назначениями техкарт, полей в нём просто нет — и это надо сказать.
 */
export function planSourceNote(source: PlanSource, year: number): string {
  if (source === 'fields') {
    return `Площади посчитаны по распределению культур на полях: что стоит на контурах в плане ${year}.`;
  }
  if (source === 'tech_cards') {
    return `План собран назначенными техкартами — площади взяты оттуда. Разложите культуры по полям, и структура пересоберётся по контурам.`;
  }
  return `В этом варианте плана ещё ничего нет: ни назначенных техкарт, ни культур на полях.`;
}

/** Почему замечаний севооборота нет — молчание тут читается как «всё чисто». */
export function rotationBasisNote(
  basis: CropStructure['rotation_basis'],
  hasNotes: boolean,
): string {
  if (basis === 'no_field_plan') {
    return 'Севооборот проверяется по полям: предшественник живёт на поле, а не на культуре. Пока план не разложен по контурам, проверять нечего.';
  }
  return hasNotes
    ? 'Система показывает нарушение и объясняет его — решение оставить или переиграть принимает агроном.'
    : 'Нарушений не нашли: возврат культур и повторные посевы в пределах нормативов справочника.';
}

/** Подпись колонки «Техкарты» — сколько вариантов назначено на культуру. */
export function techCardsLabel(row: CropStructureRow): string {
  if (row.kind === 'unassigned' || row.kind === 'dropped') return DASH;
  if (row.tech_cards_count === null) return DASH;
  if (row.tech_cards_count === 0) return 'не назначены';
  if (row.tech_cards_count === 1) return 'назначена';
  return `назначено: ${row.tech_cards_count}`;
}

/**
 * Почему на экране нет урожайности и валовки. Пустая колонка без причины
 * читается как «система не посчитала», хотя дело в правах.
 */
export function economicsHiddenNote(): string {
  return 'Плановая урожайность, валовка и техкарты вам не открыты: их даёт право на технологические карты. Структура площадей при этом полная — попросите доступ у администратора хозяйства.';
}

/**
 * План больше пашни. Раньше разница молча превращалась в ноль, и
 * перепланирование выглядело как «всё распределено».
 */
export function overplannedNote(overplannedHa: number, year: number): string {
  return `План ${year} превышает пашню хозяйства на ${formatHa(overplannedHa)} га. Либо в плане есть лишние гектары, либо в земельный банк не завели поля — доли строк в сумме дадут больше 100 %.`;
}

/** Доля «под культурами» от пашни — для плитки KPI. */
export function cropsSharePct(data: Pick<CropStructure, 'crops_area_ha' | 'arable_ha'>): number | null {
  if (!data.arable_ha) return null;
  return Math.round((data.crops_area_ha / data.arable_ha) * 100);
}
